import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  AlertTriangle,
  Ban,
  Building2,
  Calendar,
  CalendarClock,
  CheckCircle2,
  CirclePlus,
  ClipboardCheck,
  Info,
  Link2,
  Mail,
  MapPin,
  MessageSquareReply,
  Package,
  Paperclip,
  PenLine,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Truck,
  Upload,
} from "lucide-react";

import type { NotarialRequestEventType } from "@/services/notaryRequestsService";
import {
  notarialDocumentTypeTranslationKey,
  type NotarialCertificateLabelContext,
} from "@/lib/notarialDocumentTypeLabel";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ActivityEvent {
  id: string;
  type: string;
  createdAt: string;
  actorDisplayName?: string | null;
  actorRole?: string | null;
  actorUserId?: string | null;
  message?: string | null;
  data?: Record<string, unknown> | null;
}

export interface ActivityListConfig {
  /** Maps event type to icon JSX */
  icons: Record<string, JSX.Element>;
  /** Maps event type to tailwind color classes for the icon circle */
  iconColors: Record<string, string>;
  /**
   * Maps event type to translated label. The event is passed too, for the
   * labels that cannot be decided by type alone — a collapsed multi-leg
   * broadcast names the disciplines it covered.
   */
  getEventLabel: (type: string, t: TFunction, event?: ActivityEvent) => string;
  /** Extracts actor display string from an event */
  getActorLabel?: (event: ActivityEvent, t: TFunction) => string | null;
  /** Extracts detail/body text from an event */
  getDetailText?: (event: ActivityEvent, t: TFunction) => string | null;
  /** Renders custom detail content instead of blockquote for specific events */
  renderDetail?: (event: ActivityEvent, t: TFunction) => JSX.Element | null;
  /** Default icon for unknown event types */
  defaultIcon?: JSX.Element;
  defaultIconColor?: string;
}

export interface RequestActivityListProps {
  events: ActivityEvent[];
  config: ActivityListConfig;
  emptyMessage: string;
}

// ---------------------------------------------------------------------------
// TimelineRow (purely visual — identical for all request types)
// ---------------------------------------------------------------------------

function TimelineRow({
  event,
  isLast,
  config,
}: {
  event: ActivityEvent;
  isLast: boolean;
  config: ActivityListConfig;
}): JSX.Element {
  const { t } = useTranslation();

  const icon =
    config.icons[event.type] ??
    config.defaultIcon ?? <CirclePlus className="h-4 w-4" />;
  const iconColor =
    config.iconColors[event.type] ??
    config.defaultIconColor ??
    "bg-muted text-muted-foreground";

  const label = config.getEventLabel(event.type, t, event);
  const actorLabel = config.getActorLabel?.(event, t) ?? null;
  const customDetail = config.renderDetail?.(event, t) ?? null;
  const detailText = customDetail ? null : (config.getDetailText?.(event, t) ?? null);

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${iconColor}`}
        >
          {icon}
        </div>
        {!isLast && (
          <div className="mt-1 flex-1 border-l border-dashed border-border" />
        )}
      </div>
      <div className={`min-w-0 flex-1 space-y-1 ${isLast ? "" : "pb-5"}`}>
        <div className="flex min-h-8 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-foreground">{label}</span>
          <span className="text-xs text-muted-foreground">
            {new Date(event.createdAt).toLocaleString()}
          </span>
        </div>
        {actorLabel ? (
          <p className="text-xs text-muted-foreground">{actorLabel}</p>
        ) : null}
        {customDetail}
        {detailText ? (
          <blockquote className="mt-1.5 rounded-md border-l-2 border-border bg-muted/50 px-3 py-2">
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {detailText}
            </p>
          </blockquote>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function RequestActivityList({
  events,
  config,
  emptyMessage,
}: RequestActivityListProps): JSX.Element {
  const sortedEvents = useMemo(
    () =>
      [...events].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [events],
  );

  if (sortedEvents.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div>
      {sortedEvents.map((event, index) => (
        <TimelineRow
          key={event.id}
          event={event}
          isLast={index === sortedEvents.length - 1}
          config={config}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config factories
// ---------------------------------------------------------------------------

const NOTARIAL_EVENT_ICONS: Record<NotarialRequestEventType, JSX.Element> = {
  created: <CirclePlus className="h-4 w-4" />,
  accepted: <CheckCircle2 className="h-4 w-4" />,
  info_requested: <Info className="h-4 w-4" />,
  resubmitted: <RefreshCw className="h-4 w-4" />,
  certificate_uploaded: <ShieldCheck className="h-4 w-4" />,
  certificate_available: <ShieldCheck className="h-4 w-4" />,
  certificate_replaced: <RefreshCw className="h-4 w-4" />,
  correction_requested: <AlertTriangle className="h-4 w-4" />,
  cannot_complete: <Ban className="h-4 w-4" />,
  physical_document_ready: <Package className="h-4 w-4" />,
  physical_pickup_details_updated: <PenLine className="h-4 w-4" />,
  document_ready_for_delivery: <Package className="h-4 w-4" />,
  platform_received_document: <Building2 className="h-4 w-4" />,
  consumer_delivery_requested: <Mail className="h-4 w-4" />,
  consumer_delivery_details_submitted: <MapPin className="h-4 w-4" />,
  consumer_delivery_details_updated: <PenLine className="h-4 w-4" />,
  shipped_to_consumer: <Truck className="h-4 w-4" />,
  shipment_in_transit: <Truck className="h-4 w-4" />,
  delivered_to_consumer: <CheckCircle2 className="h-4 w-4" />,
  estimated_date_set: <Calendar className="h-4 w-4" />,
  estimated_date_changed: <CalendarClock className="h-4 w-4" />,
  document_uploaded: <Upload className="h-4 w-4" />,
  document_deleted: <Trash2 className="h-4 w-4" />,
};

const NOTARIAL_EVENT_ICON_COLORS: Record<NotarialRequestEventType, string> = {
  created: "bg-muted text-muted-foreground",
  accepted:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  info_requested:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  resubmitted:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  certificate_uploaded:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  certificate_available:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  certificate_replaced:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  correction_requested:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  cannot_complete: "bg-muted text-muted-foreground dark:bg-muted/80",
  physical_document_ready:
    "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
  physical_pickup_details_updated:
    "bg-violet-100 text-violet-900 dark:bg-violet-900/45 dark:text-violet-200",
  document_ready_for_delivery:
    "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  platform_received_document:
    "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  consumer_delivery_requested:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  consumer_delivery_details_submitted:
    "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300",
  consumer_delivery_details_updated:
    "bg-teal-100 text-teal-900 dark:bg-teal-900/45 dark:text-teal-200",
  shipped_to_consumer:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  shipment_in_transit:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  delivered_to_consumer:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  estimated_date_set:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  estimated_date_changed:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  document_uploaded:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  document_deleted:
    "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

/**
 * Config factory for notarial/accountant request activity timelines.
 */
export function notarialActivityConfig(
  companyActorDisplayName?: string | null,
  certificateLabelContext: NotarialCertificateLabelContext = "notary",
): ActivityListConfig {
  return {
    icons: NOTARIAL_EVENT_ICONS,
    iconColors: NOTARIAL_EVENT_ICON_COLORS,
    getEventLabel: (type, t) =>
      t(`notary.requestDetail.event_${type}`),
    getActorLabel: (event, t) => {
      const actorRoleLabel = event.actorRole
        ? t(`notary.requestDetail.actorRole_${event.actorRole}`)
        : null;
      const actorDisplay =
        event.actorRole === "company_user" && companyActorDisplayName
          ? companyActorDisplayName
          : event.actorDisplayName;
      const actorLabel = actorDisplay
        ? `${actorRoleLabel ? `${actorRoleLabel} · ` : ""}${actorDisplay}`
        : actorRoleLabel;
      return actorLabel ?? null;
    },
    getDetailText: (event, _t) => {
      const eventType = event.type as NotarialRequestEventType;
      if (
        (eventType === "document_uploaded" ||
          eventType === "document_deleted") &&
        event.message
      ) {
        // Handled by renderDetail instead
        return null;
      }
      return event.message ?? null;
    },
    renderDetail: (event, t) => {
      const eventType = event.type as NotarialRequestEventType;
      if (
        (eventType === "document_uploaded" ||
          eventType === "document_deleted") &&
        event.message
      ) {
        const [docTypeSlug, ...filenameParts] = event.message.split("|");
        const filename = filenameParts.join("|");
        const docTypeLabel = t(
          notarialDocumentTypeTranslationKey(
            docTypeSlug,
            certificateLabelContext,
          ),
          { defaultValue: docTypeSlug },
        );
        return (
          <p className="mt-1 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{docTypeLabel}</span>
            {filename ? <span className="ml-1">· {filename}</span> : null}
          </p>
        );
      }
      return null;
    },
  };
}

const ENTERPRISE_EVENT_ICONS: Record<string, JSX.Element> = {
  created: <CirclePlus className="h-4 w-4" />,
  step_completed: <CheckCircle2 className="h-4 w-4" />,
  child_linked: <Link2 className="h-4 w-4" />,
  info_requested: <Info className="h-4 w-4" />,
  info_reply: <MessageSquareReply className="h-4 w-4" />,
  status_changed: <RefreshCw className="h-4 w-4" />,
  rejected: <Ban className="h-4 w-4" />,
  approved: <CheckCircle2 className="h-4 w-4" />,
  cancelled: <Ban className="h-4 w-4" />,
};

const ENTERPRISE_EVENT_ICON_COLORS: Record<string, string> = {
  created: "bg-muted text-muted-foreground",
  step_completed:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  child_linked:
    "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400",
  info_requested:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  info_reply:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  status_changed:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  rejected:
    "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  approved:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const PAYOUT_EVENT_ICONS: Record<string, JSX.Element> = {
  created: <CirclePlus className="h-4 w-4" />,
  marked_processing: <RefreshCw className="h-4 w-4" />,
  marked_paid: <CheckCircle2 className="h-4 w-4" />,
  cancelled: <Ban className="h-4 w-4" />,
};

const PAYOUT_EVENT_ICON_COLORS: Record<string, string> = {
  created: "bg-muted text-muted-foreground",
  marked_processing:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  marked_paid:
    "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  cancelled: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

/**
 * Config factory for professional payout request activity timelines. Events
 * are synthesized from `createdAt` / `processingAt` / `paidAt` / `cancelledAt`
 * columns — there is no dedicated events table, so the caller maps timestamps
 * to `{ type, createdAt }` tuples before passing them in.
 */
/**
 * RFQ timeline config, shared by the consumer and professional surfaces.
 *
 * Both surfaces receive an ALREADY-FILTERED event list from the backend
 * (`utils/quote-request-event-visibility.ts`) — a professional never receives a
 * rival's `quote_submitted`. This config must therefore not be used to render
 * an unfiltered admin payload, which has its own dedicated timeline.
 */
const QUOTE_EVENT_ICONS: Record<string, JSX.Element> = {
  submitted: <CirclePlus className="h-4 w-4" />,
  broadcast: <Mail className="h-4 w-4" />,
  quote_submitted: <MessageSquareReply className="h-4 w-4" />,
  quote_withdrawn: <Ban className="h-4 w-4" />,
  quote_rejected: <Ban className="h-4 w-4" />,
  awarded: <CheckCircle2 className="h-4 w-4" />,
  checkout_created: <CalendarClock className="h-4 w-4" />,
  paid: <ShieldCheck className="h-4 w-4" />,
  child_request_created: <Link2 className="h-4 w-4" />,
  fulfilling: <Package className="h-4 w-4" />,
  completed: <ClipboardCheck className="h-4 w-4" />,
  fulfillment_failed: <Ban className="h-4 w-4" />,
  expired: <CalendarClock className="h-4 w-4" />,
  cancelled: <Ban className="h-4 w-4" />,
  // The consumer's own attachments. Unmapped until now, so both these rows
  // fell through to the CirclePlus default and a raw snake_case label.
  attachment_added: <Paperclip className="h-4 w-4" />,
  attachment_deleted: <Trash2 className="h-4 w-4" />,
};

const QUOTE_EVENT_ICON_COLORS: Record<string, string> = {
  submitted: "bg-muted text-muted-foreground",
  broadcast: "bg-primary/10 text-primary",
  quote_submitted: "bg-primary/10 text-primary",
  quote_withdrawn: "bg-muted text-muted-foreground",
  quote_rejected: "bg-muted text-muted-foreground",
  awarded: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  checkout_created: "bg-muted text-muted-foreground",
  paid: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  child_request_created: "bg-primary/10 text-primary",
  fulfilling: "bg-primary/10 text-primary",
  completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  fulfillment_failed: "bg-destructive/10 text-destructive",
  expired: "bg-muted text-muted-foreground",
  cancelled: "bg-destructive/10 text-destructive",
  attachment_added: "bg-primary/10 text-primary",
  attachment_deleted: "bg-destructive/10 text-destructive",
};

/**
 * @param audience whose timeline this is. The labels were written from the
 * consumer's side — a professional's own bid showed as "Presupuesto recibido",
 * which is what the CLIENT did with it, not what they did. The visibility
 * rules mean a professional only ever sees their OWN quote events, so the
 * professional wording is unambiguous where it exists; anything without a
 * `quoteEventPro_` key falls back to the shared label, which is already
 * neutral for most events.
 */
export function quoteRequestActivityConfig(
  audience: "consumer" | "professional" = "consumer",
): ActivityListConfig {
  return {
    icons: QUOTE_EVENT_ICONS,
    iconColors: QUOTE_EVENT_ICON_COLORS,
    getEventLabel: (type, t, event) => {
      if (audience === "professional") {
        const proLabel = t(`requests.user.quoteEventPro_${type}`, { defaultValue: "" });
        if (proLabel) return proLabel;
      }
      // A broadcast collapsed across legs names them, because "Enviada a
      // profesionales" twice over is what sent the consumer asking why they
      // had two identical rows. Falls through to the generic label whenever
      // the disciplines could not be resolved.
      const disciplines = event?.data?.broadcastDisciplines;
      if (type === "broadcast" && Array.isArray(disciplines) && disciplines.length > 0) {
        return t("requests.user.quoteEvent_broadcastTo", {
          disciplines: disciplines
            .map((d) => t(`requests.user.quoteDisciplinePlural_${String(d)}`))
            .join(t("requests.user.quoteDisciplineJoin")),
        });
      }
      return t(`requests.user.quoteEvent_${type}`, { defaultValue: type });
    },
    getActorLabel: (event, t) => {
      const roleLabel = event.actorRole
        ? t(`notary.requestDetail.actorRole_${event.actorRole}`, {
            defaultValue: "",
          })
        : "";
      if (event.actorDisplayName) {
        return roleLabel ? `${roleLabel} · ${event.actorDisplayName}` : event.actorDisplayName;
      }
      return roleLabel || null;
    },
    // The backend already writes a human sentence into `message`; the label
    // above carries the event name, so showing both would just repeat it.
    getDetailText: () => null,
    defaultIcon: <CirclePlus className="h-4 w-4" />,
    defaultIconColor: "bg-muted text-muted-foreground",
  };
}

export function professionalPayoutActivityConfig(): ActivityListConfig {
  return {
    icons: PAYOUT_EVENT_ICONS,
    iconColors: PAYOUT_EVENT_ICON_COLORS,
    getEventLabel: (type, t) =>
      t(`platformProfessionalPayouts.event_${type}`),
    getDetailText: (event) => event.message ?? null,
  };
}

/**
 * Config factory for enterprise request activity timelines.
 */
export function enterpriseActivityConfig(
  graph?: { nodes: Array<{ id: string; type: string; data: unknown }> } | null,
  opts?: { enterpriseName?: string; requestUserId?: string },
): ActivityListConfig {
  const enterpriseName = opts?.enterpriseName;
  const requestUserId = opts?.requestUserId;
  // Compute actor label from graph for use in detail text
  let graphActorLabel = "Participante";
  if (graph) {
    const inviteNode = graph.nodes.find((n) => n.type === "inviteActors");
    if (inviteNode) {
      graphActorLabel = (inviteNode.data as { actorLabel?: string }).actorLabel?.trim() || "Participante";
    }
  }
  // Resolve actor label from graph node if available
  function resolveActorFromGraph(nodeId: string | undefined): string | null {
    if (!nodeId || !graph) return null;
    const node = graph.nodes.find((n) => n.id === nodeId);
    if (!node) return null;
    // Staff nodes and automation nodes (run by enterprise staff)
    if (node.type === "staffApproval" || node.type === "staffAction" || node.type === "automation") return enterpriseName ?? "Empresa";
    // Participant nodes — look up actorSource → inviteActors.actorLabel
    const actorSource = (node.data as Record<string, unknown> | undefined)?.actorSource as string | undefined;
    if (actorSource) {
      const inviteNode = graph.nodes.find((n) => n.id === actorSource && n.type === "inviteActors");
      const label = ((inviteNode?.data as Record<string, unknown> | undefined)?.actorLabel as string | undefined)?.trim();
      if (label) return label;
    }
    return null;
  }

  return {
    icons: ENTERPRISE_EVENT_ICONS,
    iconColors: ENTERPRISE_EVENT_ICON_COLORS,
    getEventLabel: (type, t) =>
      t(`requests.user.enterprise.event_${type}`),
    getActorLabel: (event, t) => {
      // Use actor label from event data if available (e.g. "Arrendador", "Empresa")
      const actorLabel = event.data?.actorLabel as string | undefined;
      if (actorLabel) return actorLabel;
      // Staff-originated events: show enterprise name
      const STAFF_EVENT_TYPES = new Set(["info_requested", "status_changed", "rejected"]);
      if (STAFF_EVENT_TYPES.has(event.type) && enterpriseName) {
        return enterpriseName;
      }
      // Fall back to resolving from graph using nodeId
      if (event.type === "step_completed" && event.data?.nodeId) {
        const graphLabel = resolveActorFromGraph(event.data.nodeId as string);
        if (graphLabel) return graphLabel;
      }
      // If the actor is not the requester, show enterprise name
      if (event.actorUserId && requestUserId && event.actorUserId !== requestUserId && enterpriseName) {
        return enterpriseName;
      }
      return event.actorUserId
        ? t("requests.user.enterprise.actorUser")
        : null;
    },
    getDetailText: (event) => {
      if (
        event.type === "step_completed" &&
        (event.data?.nodeLabel || event.data?.nodeId)
      ) {
        if (event.data.nodeLabel) {
          // Append actor info for per-actor automation runs
          if (event.data.actorIndex != null) {
            return `${event.data.nodeLabel as string} — ${graphActorLabel} ${Number(event.data.actorIndex) + 1}`;
          }
          return event.data.nodeLabel as string;
        }
        // Resolve actor-keyed nodeIds (e.g. "internal-cert_0") to a readable label
        const rawId = event.data.nodeId as string;
        if (graph) {
          const actorMatch = rawId.match(/^(.+)_(\d+)$/);
          const baseId = actorMatch ? actorMatch[1] : rawId;
          const actorIdx = actorMatch ? Number(actorMatch[2]) : null;
          const node = graph.nodes.find((n: { id: string }) => n.id === baseId);
          if (node) {
            const baseLabel = (node.data as { label?: string }).label ?? baseId;
            // Per-actor automation events carry actorIndex in data
            const eventActorIdx = event.data.actorIndex as number | undefined;
            if (eventActorIdx != null) {
              return `${baseLabel} — ${graphActorLabel} ${eventActorIdx + 1}`;
            }
            if (actorIdx !== null) {
              return `${baseLabel} — ${graphActorLabel} ${actorIdx + 1}`;
            }
            return baseLabel;
          }
        }
        // Fallback: still check actorIndex even without graph
        if (event.data.actorIndex != null) {
          return `${rawId} — ${graphActorLabel} ${Number(event.data.actorIndex) + 1}`;
        }
        return rawId;
      }
      if (event.type === "status_changed" && event.data?.to) {
        const STATUS_LABELS: Record<string, string> = {
          pending: "Pendiente",
          in_review: "En revisión",
          awaiting_action: "Acción requerida",
          needs_info: "Info. solicitada",
          approved: "Aprobada",
          rejected: "Rechazada",
          cancelled: "Cancelada",
          completed: "Completada",
          awaiting_child: "Esperando sub-solicitud",
        };
        const toLabel = STATUS_LABELS[event.data.to as string] ?? event.data.to;
        return toLabel as string;
      }
      if (event.type === "info_requested" && event.data?.message) {
        return event.data.message as string;
      }
      if (event.type === "info_reply" && event.data?.message) {
        return event.data.message as string;
      }
      return null;
    },
  };
}
